import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  getAcademicYearById,
  updateAcademicYear,
} from "../../../api/generalParam";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type UUID = string;
type Props = {
  yearID: UUID;
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const EditAcademicYearModal = ({ yearID, show, onClose, onSaved }: Props) => {
  const labelSchema = z
    .string()
    .regex(/^\d{4}-\d{4}$/, "Le format doit être AAAA-AAAA, ex: 2024-2025")
    .nonempty("Libélle requis pour l'année académique")
    .refine(
      (val) => {
        const [start, end] = val.split("-").map(Number);
        return start + 1 === end;
      },
      {
        message: "Les années doivent être consécutives, ex: 2024-2025",
      },
    );

  const createSchema = z.object({
    label: labelSchema,
    startDate: z.string().nonempty("Date de début requis"),
    endDate: z.string().nonempty("Date de fin requis"),
    statut: z.boolean(),
  });

  type FormFields = z.infer<typeof createSchema>;
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'. année académique
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour modification vers le backend
      await updateAcademicYear(data, yearID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Année académique mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur
      // console.log(error);
      setError("root", {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      });
    }
  };

  useEffect(() => {
    if (!show || !yearID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchYear = async () => {
      try {
        const response = await getAcademicYearById(yearID);
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Année académique introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          label: response.data.label,
          startDate: response.data.startDate,
          endDate: response.data.endDate,
          statut: response.data.statut,
        });
      } catch (error) {
        onClose(); // On ferme le modal
        // console.log(error)
      }
    };

    fetchYear();
  }, [show, yearID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier une Année Académique </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Libellé de l'année"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer02">
                    Date Début
                  </label>
                  <input
                    {...register("startDate")}
                    type="date"
                    className={
                      errors.startDate
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Début de l'année"
                  />
                  {errors.startDate && (
                    <div className="invalid-feedback">
                      {errors.startDate.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer03">
                    Date Fin
                  </label>
                  <input
                    {...register("endDate")}
                    type="date"
                    className={
                      errors.endDate
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Fin de l'année"
                  />
                  {errors.endDate && (
                    <div className="invalid-feedback">
                      {errors.endDate.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="form-check form-check-lg form-switch mb-3">
                  <label className="form-check-label" htmlFor="switch-lg">
                    Statut de l'année
                  </label>
                  <input
                    {...register("statut")}
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switch-lg"
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditAcademicYearModal,
  [buildPermission(Resource.ACADEMIC_YEAR, Action.UPDATE)],
  "masquer",
);
